import dayjs from 'dayjs'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppContext } from '../../../../contexts/AppContext'
import useUser from '../../../../contexts/hooks/useUser'
import { backendCall } from '../../../../modules/backendApi'
import { trpc } from '../../../../modules/trpc'
import { HvacContext } from '../HvacContext'
import { BlockGenerator, getBackground } from './createHvacBlocks'

const HeatingControls = () => {
	const { isDarkMode } = useContext(AppContext)
	const { hvac, smartPolicy, thermoStatus, smartStatus, onlyDaySchedule, dayAndNightSchedule } =
		useContext(HvacContext)

	const { user } = useUser()

	const { t } = useTranslation('pages_hvac')

	const { data: userSettings } = trpc.user.userSettings.get.useQuery(undefined, {
		enabled: !!user?.id,
	})

	const [monitoredSetPoint, setMonitoredSetPoint] = useState(0)
	const [smartSavingHours, setSmartSavingHours] = useState<number[]>([])

	useMemo(() => {
		const fetchSpotPrices = async () => {
			const spotPrices = await backendCall.getExchangePrices(
				dayjs().startOf('day').format(),
				dayjs().endOf('day').format()
			)

			// Group by hour and calculate average price for each full hour
			const hourlyPrices = new Map<number, number[]>()

			spotPrices.forEach(price => {
				const hour = price.localTime.hour
				const existingPrices = hourlyPrices.get(hour)
				if (existingPrices) {
					existingPrices.push(price.priceWithAlv)
				} else {
					hourlyPrices.set(hour, [price.priceWithAlv])
				}
			})

			// Get 3 most expensive full hours
			const highPriceHours = Array.from(hourlyPrices.entries())
				.map(([hour, prices]) => ({
					hour,
					avgPrice: prices.reduce((sum, price) => sum + price, 0) / prices.length,
				}))
				.sort((a, b) => b.avgPrice - a.avgPrice)
				.slice(0, 3)
				.map(item => item.hour)
				.sort((a, b) => a - b)
			setSmartSavingHours(highPriceHours)
		}
		fetchSpotPrices()
	}, [])

	const intervalStrings = smartStatus?.intervals
		.map(interval => `${interval.from}-${interval.to}`)
		.join(', ')

	const displayTemperature = () => {
		if (hvac?.thermostatState.mode === 'HEAT') {
			return hvac?.thermostatState.heatSetpoint
		}
		if (hvac?.thermostatState.mode === 'COOL') {
			return hvac?.thermostatState.coolSetpoint
		}
		if (hvac?.thermostatState.mode === 'OFF') {
			return 0
		}
	}

	const setMonitored = () => {
		if (smartPolicy) {
			if (onlyDaySchedule?.isEnabled) {
				setMonitoredSetPoint(onlyDaySchedule.rules[0].targetState.heatSetpoint)
			}
			if (dayAndNightSchedule?.isEnabled) {
				setMonitoredSetPoint(dayAndNightSchedule.rules[0].targetState.heatSetpoint)
			}
		} else {
			if (hvac?.thermostatState.mode === 'HEAT' || hvac?.thermostatState.mode === 'AUTO') {
				setMonitoredSetPoint(hvac.thermostatState.heatSetpoint || 0)
			}
			if (hvac?.thermostatState.mode === 'COOL') {
				setMonitoredSetPoint(hvac.thermostatState.coolSetpoint || 0)
			}
		}
	}

	useEffect(() => {
		setMonitored()
	}, [hvac?.thermostatState, onlyDaySchedule, dayAndNightSchedule, smartPolicy])

	useMemo(() => {
		displayTemperature()
		setMonitored()
	}, [hvac?.thermostatState, onlyDaySchedule, dayAndNightSchedule])

	return (
		<div
			className='block-holder'
			style={{
				background: getBackground(monitoredSetPoint, isDarkMode),
			}}
		>
			{smartPolicy && smartStatus?.intervals && smartStatus.intervals.length > 0 && (
				<div className='information-text'>
					<div className='texts'>
						<div className='icon' style={{ marginRight: '0.35em' }}>
							<img
								src='/assets/icons/thermostat.svg'
								alt={t('heatingControls.altThermoStatStatusIcon')}
								color='blue'
								style={{
									width: '1.6em',
									height: '1.6em',
								}}
							/>
						</div>
						<div className='title'>
							{t('heatingControls.smartHeating')} <br /> {t('heatingControls.clock')}:{' '}
							{intervalStrings}
						</div>
					</div>
				</div>
			)}

			<div className='info relative flex flex-col items-center'>
				<BlockGenerator count={monitoredSetPoint} isDarkMode={isDarkMode} />

				<div className='display'>
					<div className='texts text-center'>
						<div className='temperature font-semibold text-5xl mt-14 mb-8'>
							{monitoredSetPoint}°C
						</div>
						<div className='text-text-grey-200 text-xl font-semibold mb-2'>{t('temperature')}</div>
						<span className=' text-text-grey-200 text-md'>
							{t('now')} {hvac?.temperatureState.currentTemperature}°C
						</span>
					</div>
				</div>
			</div>

			<div className='relative -mt-4 flex flex-col gap-4 mb-16 items-center' style={{ zIndex: 1 }}>
				<div className='current-status w-fit text-text-dark dark:text-white bg-white dark:bg-[#144448] border border-[#e9e9e9] dark:border-[#acd7d4]'>
					<div className='stuff flex items-center'>
						<div className='icon'>{thermoStatus.icon}</div>
						<div className='status-text'>{thermoStatus.text}</div>
					</div>
				</div>

				{userSettings?.smartHeatingEnabled && (
					<div className='todays-smart-heating-hours'>
						{smartSavingHours.length > 0 && (
							<div className='info-text text-text-dark dark:text-white bg-white dark:bg-[#144448] border border-[#e9e9e9] dark:border-[#acd7d4] rounded-xl px-4 py-3 shadow-sm'>
								<div className='flex items-center gap-2 mb-2'>
									<div className='w-2 h-2 bg-orange-500 rounded-full'></div>
									<div className='text-sm font-medium text-gray-700 dark:text-gray-200'>
										{t('heatingControls.smartSavingHours')}
									</div>
								</div>
								<div className='flex flex-wrap justify-center gap-2'>
									{smartSavingHours.map(hour => {
										const currentHour = dayjs().tz('Europe/Helsinki').hour()
										const isCurrentHour = hour === currentHour

										return (
											<span
												key={hour}
												className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium border ${
													isCurrentHour
														? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700'
														: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-700'
												}`}
											>
												{`${hour.toString().padStart(2, '0')}:00`}
											</span>
										)
									})}
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export default HeatingControls
